import { useRef, useEffect, useMemo } from 'react'
import { debounce, type DebounceSettings } from 'lodash'

/**
 * A hook that debounces a callback function. Needed to avoid unnecessary re-renders and other react-related bugs when using debounce.
 * @param callback - The callback function to debounce.
 * @param delay - The delay in milliseconds before the callback is executed.
 * @param options - The options for the debounce function.
 * @returns The debounced callback function.
 *
 * @example
 * const callback = (name: string) => {
 *   console.log(name)
 * }
 * const debouncedCallback = useDebounce(callback, 1000, { leading: true, trailing: false })
 * debouncedCallback('Hello, world!')
 */
export const useDebounce = <Arguments extends unknown[]>(
  callback: (...callbackArguments: Arguments) => void,
  delay: number,
  options?: DebounceSettings
) => {
  const callbackReference = useRef<typeof callback>(null)

  useEffect(() => {
    callbackReference.current = callback
  }, [callback])

  const debouncedCallback = useMemo(() => {
    const debouncedFunction = (...callbackArguments: Arguments) => {
      callbackReference.current?.(...callbackArguments)
    }
    return debounce(debouncedFunction, delay, options)
  }, [delay, options])

  return debouncedCallback
}
